import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useContext, useState } from 'react';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { OrganisationSettings } from '../../../../interfaces/delivery-company/orders';

interface InvoiceSettlementConfigProps {
  settings: OrganisationSettings;
}

function InvoiceSettlementConfig({ settings }: InvoiceSettlementConfigProps) {
  const { config } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [settlementMode, setSettlementMode] = useState<'NET' | 'GROSS'>(
    settings.default_driver_commission_settlement_mode || 'NET'
  );

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await axios.post(
        `${API_ENDPOINT}/api/v1/settings`,
        {
          default_driver_commission_settlement_mode: settlementMode,
        },
        config
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setError('Failed to save settings: ' + e.message);
      } else {
        setError('Failed to save settings');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Configure how driver commissions are handled in invoices by default.
        This setting can be overridden per driver or per invoice.
      </Typography>

      <Box  sx={{ mb: 3 }}>

        <RadioGroup
          value={settlementMode}
          onChange={(e) => setSettlementMode(e.target.value as 'NET' | 'GROSS')}
        >
          <FormControlLabel
            value="NET"
            control={<Radio />}
            label={
              <Box m={1}>
                <Typography variant="body1" fontWeight="bold">
                  Net Settlement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Commissions are automatically deducted from collections in a single invoice.
                  <br />
                  Example: Driver collects $100 COD, earns $20 commission → Invoice shows driver owes $80
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="GROSS"
            control={<Radio />}
            label={
              <Box m={1}>
                <Typography variant="body1" fontWeight="bold">
                  Gross Settlement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Collections and commissions are tracked in separate invoices.
                  <br />
                  Example: Driver collects $100 COD, earns $20 commission → Two invoices: Driver owes $100 (collection), Driver is owed $20 (commission)
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Box width={"100%"} textAlign={"center"}>
        {
          loading ?<CircularProgress size={32} /> : <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || settlementMode === settings.default_driver_commission_settlement_mode}
        >
          Save
        </Button>
        }
      </Box>
      

    </Box>
  );
}

export default InvoiceSettlementConfig;



